import axios from 'axios';
import { fetchUserOrgs, type GithubOrgSummary } from '@/apis/githubUserOrgs';

export interface BitbucketWorkspace {
  slug: string;
  name: string;
  uuid?: string;
  [key: string]: unknown;
}

/**
 * An org as `GET /user/orgs` returns it.
 *
 * `id` is the provider account id. It is what GitHub's install page accepts as
 * `suggested_target_id`, so carrying it here is what lets a caller send someone
 * to the right org's installation rather than whichever org happens to be first
 * in the workspace's scope.
 */
export interface GithubMemberOrg {
  login: string;
  id?: number;
}

/**
 * Cache of the member-org listing per token, so typing doesn't refetch.
 *
 * Keyed by token rather than held as a single module-level promise: switching
 * account without a reload would otherwise serve the previous user's orgs.
 */
const githubOrgCache = new Map<string, Promise<GithubMemberOrg[]>>();

/**
 * Drop the cached listing so the next `searchGithubOrgs` re-reads `/user/orgs`.
 *
 * The cache is keyed by token, and granting this app access to an organization
 * on github.com does not change the token — so a caller that sends someone off
 * to grant access and refetches on their return would be served the same cached
 * promise, and the newly approved org would never appear without a hard reload.
 * Only that flow should call this; ordinary search and onboarding reads keep
 * using the cache, which is what stops typing from refetching.
 *
 * @param accessToken - Clear this token's entry, or every entry when omitted.
 */
export const invalidateGithubOrgCache = (accessToken?: string) => {
  if (accessToken) githubOrgCache.delete(accessToken);
  else githubOrgCache.clear();
};

const fetchGithubMemberOrgs = async (accessToken: string) => {
  // Own membership, `read:org` only, and it runs before a workspace exists, so
  // this stays a direct GitHub read rather than a backend hop. `fetchUserOrgs`
  // follows GitHub's Link header instead of guessing from page size.
  const orgs = await fetchUserOrgs(accessToken);
  return orgs
    .filter((org): org is GithubOrgSummary & { login: string } =>
      Boolean(org.login)
    )
    .map((org) => ({ login: org.login, id: org.id }));
};

/**
 * Organizations the authenticated user is actually a member of, filtered by
 * `searchQuery` (empty query returns the full list so the dropdown can open
 * populated).
 *
 * Reads the user's own memberships, NOT GitHub's global org search: the
 * returned value becomes `OrganizationModel.git_org_name`, which scopes every
 * repo the workspace may attach. Searching all of GitHub let anyone claim any
 * org, and because the backend enforces one org per workspace, a squatted claim
 * also locked out the real owner.
 *
 * Membership is the bar, not ownership: any member may set up the workspace.
 *
 * @param accessToken - The session's GitHub token.
 */
export const searchGithubOrgs = async (
  searchQuery: string,
  accessToken: string
) => {
  try {
    let listing = githubOrgCache.get(accessToken);
    if (!listing) {
      listing = fetchGithubMemberOrgs(accessToken);
      githubOrgCache.set(accessToken, listing);
    }
    const orgs = await listing;
    const needle = searchQuery.trim().toLowerCase();
    return needle
      ? orgs.filter((org) => org.login.toLowerCase().includes(needle))
      : orgs;
  } catch (error) {
    // A failed fetch must not be cached, or one blip breaks the picker for the
    // whole session.
    githubOrgCache.delete(accessToken);
    console.error('Error fetching GitHub organizations:', error);
    // Rethrown, not swallowed to []: a caller that can't tell a transient
    // failure apart from "this user really has zero orgs" may wrongly treat
    // the failure as confirmation there's nothing to link. Callers that want
    // the old "just show nothing" behavior add their own .catch(() => []).
    throw error;
  }
};

export const searchGitlabGroups = async (
  searchQuery: string,
  accessToken: string,
  authUrl?: string
) => {
  if (authUrl) {
    // Ensure 'all_available=true' is present in authUrl as query param
    const urlObj = new URL(authUrl);
    urlObj.searchParams.set('all_available', 'true');
    authUrl = urlObj.toString();
  }
  try {
    const response = await axios.get(
      `${authUrl || 'https://gitlab.com'}/api/v4/groups?search=${searchQuery}&all_available=true`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );

    return response?.data;
  } catch (error) {
    console.error('Error fetching GitLab groups:', error);
    return [];
  }
};

export const searchBitbucketWorkspaces = async (
  searchQuery: string,
  accessToken: string
): Promise<BitbucketWorkspace[]> => {
  try {
    // GET /2.0/user/workspaces — the old /2.0/user/permissions/workspaces
    // endpoint was removed (410 Gone) per Bitbucket CHANGE-2770.
    // The new endpoint returns flat workspace objects in values[]:
    //   { type, uuid, slug, name, links }
    // The ~ (contains) operator is NOT supported on this endpoint, so we
    // fetch all workspaces and filter client-side.
    const allWorkspaces: BitbucketWorkspace[] = [];
    let nextUrl: string | null =
      `/api/bitbucket/proxy/user/workspaces?pagelen=100`;
    const MAX_PAGES = 20;
    let page = 0;

    while (nextUrl && page++ < MAX_PAGES) {
      // `response` and `data` are annotated to break a circular inference:
      // `nextUrl` is reassigned further down from a value derived from `data`,
      // and under @types/node 22 the global `fetch` overloads need `nextUrl`'s
      // type resolved to pick a signature, so TS reports TS7022 on all four
      // locals unless the chain is pinned here.
      const response: Response = await fetch(nextUrl, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!response.ok) {
        if (process.env.NODE_ENV === 'development') {
          console.warn(
            '[searchBitbucketWorkspaces] Bitbucket proxy request failed:',
            nextUrl,
            response.status
          );
        }
        break;
      }
      const data: { values?: Array<Record<string, unknown>>; next?: string } =
        await response.json();
      // The new /user/workspaces endpoint returns flat workspace objects in
      // values[].  Defensively handle the old nested shape ({ workspace: {...} })
      // in case the API response varies.
      const entries: Array<Record<string, unknown>> = Array.isArray(
        data?.values
      )
        ? data.values
        : [];
      for (const entry of entries) {
        const nested = entry.workspace as Record<string, unknown> | undefined;
        const slug = (entry.slug ?? nested?.slug) as string | undefined;
        const name = (entry.name ?? nested?.name ?? slug) as string | undefined;
        const uuid = (entry.uuid ?? nested?.uuid) as string | undefined;
        if (slug) {
          allWorkspaces.push({ slug, name: name ?? slug, uuid });
        }
      }
      // Follow pagination — rewrite absolute Bitbucket URL through the proxy.
      if (data.next) {
        try {
          const parsed = new URL(data.next);
          const match = parsed.pathname.match(/\/2\.0\/(.+)/);
          nextUrl = match
            ? `/api/bitbucket/proxy/${match[1]}${parsed.search}`
            : null;
        } catch {
          nextUrl = null;
        }
      } else {
        nextUrl = null;
      }
    }

    // Client-side filtering: case-insensitive substring match on slug or name.
    const query = searchQuery.trim().toLowerCase();
    if (!query) return allWorkspaces;
    return allWorkspaces.filter(
      (ws) =>
        ws.slug.toLowerCase().includes(query) ||
        (ws.name?.toLowerCase().includes(query) ?? false)
    );
  } catch {
    return [];
  }
};

export const searchSelfHostedProvider = async (
  searchQuery: string,
  accessToken: string,
  url: string
) => {
  try {
    const response = await axios.get(
      `${url}/api/v4/groups?search=${searchQuery}&all_available=true`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );

    return response?.data;
  } catch (error) {
    console.error('Error fetching self-hosted provider:', error);
    return [];
  }
};
